/**
 * Implied volatility and greeks — ROADMAP 6.6.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BLACK-76 ON THE FORWARD, NOT BLACK-SCHOLES ON THE SPOT
 *
 * The textbook model prices an option off the SPOT of its underlying and a
 * dividend yield. For an Indian index option that is the wrong input twice
 * over: NIFTY pays no dividend that anyone models, and the instrument the
 * option actually tracks is the FUTURES contract of its own expiry, which
 * trades at a basis to the index that moves on its own.
 *
 * Feed the cash index into Black-Scholes and every call comes out
 * systematically over-valued and every put under-valued by the basis, so the
 * call IV reads high, the put IV reads low, and the skew is an artefact of
 * the input rather than a fact about the market. On a 22,000 index with a 60
 * point basis that is visible at the first strike.
 *
 * Black-76 takes the forward directly. It is the same mathematics with F in
 * place of S·e^((r−q)T), and it removes the dividend assumption entirely
 * because the basis already contains it. When no futures price is at hand,
 * `forwardFromParity` recovers the forward the market is actually using from
 * a call and a put at the same strike — which is how an IV surface is built
 * in practice, and is self-consistent by construction.
 *
 * THE UNITS ARE FIXED HERE, ONCE
 *
 * Raw Black-76 gives vega per 1.00 of volatility and theta per YEAR, which
 * nobody reads. Every caller that scaled them itself would be a chance to
 * divide by 365 in one place and 252 in another — the shape of the P&L
 * double-subtraction 6.4's header describes. So this module returns what a
 * trader reads: vega per VOL POINT, theta per CALENDAR DAY, rho per
 * PERCENTAGE POINT of rate. Nothing downstream rescales.
 *
 * AN UNSOLVABLE QUOTE IS SAID OUT LOUD
 *
 * A price below intrinsic has no implied volatility — not a low one, none.
 * It happens constantly: a stale LTP on a strike that has not traded, a
 * crossed book, a snapshot taken mid-auction. The solver returns a refusal
 * with a reason rather than a number, because a chain column showing "0.1%"
 * for an unpriceable strike is worse than one showing a dash.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/** Milliseconds in a calendar year, matching the 365-day convention below. */
const YEAR_MS = 365 * 24 * 60 * 60 * 1000;
/**
 * NSE and BSE derivatives settle at 15:30 IST on the expiry day.
 *
 * IST is a fixed +05:30 with no daylight saving, ever, so 15:30 IST is 10:00
 * UTC on that calendar date and the conversion needs no timezone database.
 */
const EXPIRY_UTC_HOUR = 10;
/**
 * Time to expiry in YEARS, on a calendar-day basis.
 *
 * Calendar days, not trading days, because that is the convention every
 * broker's own greeks display uses and a number that disagrees with the
 * broker's terminal gets distrusted whether or not it is better. The known
 * cost is that a Friday close and a Monday open differ by three days of
 * decay that the market prices over one, so Monday IV reads low.
 *
 * Returns 0 at or after settlement. Never negative: a negative T produces
 * NaN greeks that propagate silently.
 */
export function yearsToExpiry(expiry, now = new Date()) {
    const settleMs = Date.UTC(expiry.getUTCFullYear(), expiry.getUTCMonth(), expiry.getUTCDate(), EXPIRY_UTC_HOUR);
    const left = settleMs - now.getTime();
    return left <= 0 ? 0 : left / YEAR_MS;
}
/**
 * The standard normal CDF, to double precision.
 *
 * Hart's rational approximation. The cheap Abramowitz-Stegun polynomial is
 * accurate to about 1e-7, which is fine for a price and NOT fine inside an
 * IV solver: the error feeds the root-finder, and on a deep wing where vega
 * is small it moves the answer by whole volatility points.
 */
export function normalCdf(x) {
    const z = Math.abs(x);
    if (z > 37)
        return x > 0 ? 1 : 0;
    const e = Math.exp((-z * z) / 2);
    let p;
    if (z < 7.07106781186547) {
        let b = 3.52624965998911e-2 * z + 0.700383064443688;
        b = b * z + 6.37396220353165;
        b = b * z + 33.912866078383;
        b = b * z + 112.079291497871;
        b = b * z + 221.213596169931;
        b = b * z + 220.206867912376;
        let c = 8.83883476483184e-2 * z + 1.75566716318264;
        c = c * z + 16.064177579207;
        c = c * z + 86.7807322029461;
        c = c * z + 296.564248779674;
        c = c * z + 637.333633378831;
        c = c * z + 793.826512519948;
        c = c * z + 440.413735824752;
        p = (e * b) / c;
    }
    else {
        // Continued fraction, for the far tail where the rational form loses it.
        let b = z + 0.65;
        b = z + 4 / b;
        b = z + 3 / b;
        b = z + 2 / b;
        b = z + 1 / b;
        p = e / (b * 2.506628274631);
    }
    return x > 0 ? 1 - p : p;
}
/** The standard normal PDF. */
const normalPdf = (x) => Math.exp((-x * x) / 2) / 2.5066282746310002;
/**
 * Black-76 price.
 *
 * At T = 0 or vol = 0 the option is worth its discounted intrinsic value and
 * the formula divides by zero, so both are answered directly rather than
 * guarded with an epsilon that makes a real quote slightly wrong.
 */
export function black76Price(input) {
    const { forward, strike, years, rate, vol, optionType } = input;
    if (!(forward > 0) || !(strike > 0))
        return 0;
    const discount = Math.exp(-rate * years);
    if (years <= 0 || vol <= 0) {
        const intrinsic = optionType === 'CE' ? Math.max(0, forward - strike) : Math.max(0, strike - forward);
        return discount * intrinsic;
    }
    const sqrtT = Math.sqrt(years);
    const d1 = (Math.log(forward / strike) + (vol * vol * years) / 2) / (vol * sqrtT);
    const d2 = d1 - vol * sqrtT;
    /*
     * THE OUT-OF-THE-MONEY LEG IS COMPUTED; THE IN-THE-MONEY ONE IS DERIVED.
     *
     * Both closed forms subtract two large nearly-equal products, and on a deep
     * in-the-money option that cancellation eats most of the significant
     * digits: pricing a 24,000 put against a 22,000 forward subtracts 22,000
     * from 24,000 and then asks for a time value of 1e-11, which lands below
     * the rounding noise of the subtraction. The computed price then stops
     * being monotone in volatility, and an IV solver walking that noise
     * converges on an arbitrary point in it — measured as 8.65% for an option
     * generated at 8.00%.
     *
     * Put-call parity holds EXACTLY, so the far side is reconstructed by adding
     * one exact term to the well-conditioned near side instead.
     */
    const callOtm = strike >= forward;
    const call = discount * (forward * normalCdf(d1) - strike * normalCdf(d2));
    const put = discount * (strike * normalCdf(-d2) - forward * normalCdf(-d1));
    const parity = (forward - strike) * discount;
    if (optionType === 'CE')
        return callOtm ? call : put + parity;
    return callOtm ? call - parity : put;
}
/** Discounted intrinsic value — the floor of any option's price. */
function intrinsicValue(input) {
    const { forward, strike, years, rate, optionType } = input;
    const payoff = optionType === 'CE' ? Math.max(0, forward - strike) : Math.max(0, strike - forward);
    return payoff * Math.exp(-rate * years);
}
/**
 * Greeks at a given volatility.
 *
 * PER UNIT of the option, never per lot. The lot-size multiplication belongs
 * where the position is read, once — two places doing it is a money bug, and
 * a basket delta that is 75x out looks plausible enough to act on.
 */
/**
 * The risk-neutral probability that an option expires IN THE MONEY — N(d2).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SAME d2 AS THE TEXTBOOK SPOT FORMULA, VIA A BETTER FORWARD
 *
 * The form usually quoted is on SPOT, with the carry written out:
 *
 *     d2 = [ ln(S/K) + (r - sigma^2/2) t ] / (sigma sqrt(t))
 *
 * and the form here is on the FORWARD:
 *
 *     d2 = [ ln(F/K) - (sigma^2/2) t ] / (sigma sqrt(t))
 *
 * They are the same equation. Substituting F = S e^(rt) gives
 * ln(F/K) = ln(S/K) + rt, and the two collapse into one another exactly.
 *
 * The forward is used because this project does NOT get it from S e^(rt).
 * `forwardFromParity` recovers it from the call and put at one strike, which
 * means it carries whatever carry and dividend the MARKET is actually
 * pricing. NIFTY pays dividends; assuming carry equals the risk-free rate
 * puts the forward above where it trades and biases every probability on the
 * chain in the same direction. Reading the forward out of parity has no such
 * assumption in it.
 *
 * WHY THIS IS NOT DELTA, THOUGH IT LOOKS LIKE IT
 *
 * Delta is N(d1) and this is N(d2), and d2 = d1 - sigma sqrt(t). They are
 * close enough to be mistaken for each other on a screen — around 0.01 apart
 * on a weekly — and far enough apart to disagree in the last rendered digit,
 * which is exactly why both are worth showing. Delta answers "how much does
 * this move with the underlying"; this answers "how likely is it to finish
 * worth something". A trader sizing a short strangle is asking the second.
 *
 * Returned as a FRACTION in [0, 1]. The caller renders it as a percentage.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function probabilityItm(input) {
    const { forward, strike, years, vol, optionType } = input;
    if (!(forward > 0) || !(strike > 0) || !(vol > 0))
        return null;
    /*
     * AT EXPIRY IT IS NOT A PROBABILITY, IT IS A FACT.
     *
     * Returning 0.5 for an at-the-money expiring option would be a model
     * answer to a question the model no longer governs. Intrinsic value
     * decides it, and a strike exactly at the forward is reported as out —
     * the same tie-break `black76Price` makes with `Math.max(0, ...)`.
     */
    if (years <= 0) {
        return optionType === 'CE' ? (forward > strike ? 1 : 0) : (strike > forward ? 1 : 0);
    }
    const sqrtT = Math.sqrt(years);
    const d2 = (Math.log(forward / strike) - (vol * vol * years) / 2) / (vol * sqrtT);
    if (!Number.isFinite(d2))
        return null;
    // N(d2) for a call; the complement for a put, because exactly one of them
    // finishes in the money.
    return optionType === 'CE' ? normalCdf(d2) : normalCdf(-d2);
}
export function greeksOf(input) {
    const { forward, strike, years, rate, vol, optionType } = input;
    const FLAT = { delta: 0, gamma: 0, vega: 0, theta: 0, rho: 0 };
    if (!(forward > 0) || !(strike > 0) || years <= 0 || vol <= 0) {
        /*
         * An expired or zero-vol option has no greeks worth reporting. Delta is
         * arguably 1 or 0 by moneyness, but gamma is a delta function and theta
         * is undefined — reporting one of the five and inventing the rest is
         * less useful than saying the model does not apply.
         */
        return FLAT;
    }
    const discount = Math.exp(-rate * years);
    const sqrtT = Math.sqrt(years);
    const d1 = (Math.log(forward / strike) + (vol * vol * years) / 2) / (vol * sqrtT);
    if (!Number.isFinite(d1))
        return FLAT;
    const pdf = normalPdf(d1);
    const delta = optionType === 'CE' ? discount * normalCdf(d1) : -discount * normalCdf(-d1);
    const gamma = (discount * pdf) / (forward * vol * sqrtT);
    // Per 1.00 of vol, then scaled to one vol point.
    const vegaPerUnitVol = discount * forward * pdf * sqrtT;
    const price = black76Price(input);
    // Per year: r x V - e^(-rT) F n(d1) sigma / (2 sqrt(T)), then per day.
    const thetaPerYear = rate * price - (discount * forward * pdf * vol) / (2 * sqrtT);
    return {
        delta,
        gamma,
        vega: vegaPerUnitVol / 100,
        theta: thetaPerYear / 365,
        // Under Black-76 with F held fixed, the rate only discounts: dV/dr = -T V.
        rho: (-years * price) / 100,
    };
}
/** Volatility bounds for the search, as fractions: 0.1% to 1000%. */
const VOL_MIN = 0.001;
const VOL_MAX = 10;
/** Half a paisa on the price. Used only for the WALL checks below. */
const PRICE_TOLERANCE = 0.005;
/**
 * How tight the volatility bracket must get: a hundredth of a vol point.
 *
 * The stopping rule is the BRACKET, not the price. Stopping on "the price is
 * within half a paisa" returns whichever volatility first happened to price
 * close enough, and on a low-vega strike that band is wide: a 1,500-point ITM
 * call seven days out moves less than a paisa over a whole vol point, so the
 * price rule returned 13.77% for an option generated at 14.00%. Bisecting the
 * bracket instead converges on the root itself, and the price tolerance has
 * no say in the answer.
 *
 * The sensitivity is real and does not go away: on such a strike the market's
 * own paise of resolution cannot pin the IV either. What this guarantees is
 * that the SOLVER adds nothing to it.
 */
const VOL_TOLERANCE = 1e-4;
const MAX_ITERATIONS = 200;
/**
 * Implied volatility, by BISECTION.
 *
 * Newton-Raphson is faster and is the wrong tool here. Its step divides by
 * vega, and vega goes to zero on exactly the strikes that need solving most
 * — a far wing minutes from expiry — where the iteration takes an enormous
 * step, leaves the bracket and either diverges or converges to nonsense that
 * is indistinguishable from an answer.
 *
 * Price is strictly increasing in volatility, so bisection on a bracket that
 * contains the root cannot fail and cannot leave it. 100 halvings of
 * [0.001, 10] resolve far past double precision. The cost is tens of
 * microseconds per strike, which a fixed refresh cadence absorbs.
 */
export function impliedVol(input) {
    const { price, forward, strike, years, rate, optionType } = input;
    if (!Number.isFinite(price) || price <= 0)
        return { solved: false, why: 'No price.' };
    if (!(forward > 0))
        return { solved: false, why: 'No forward price for this expiry.' };
    if (!(strike > 0))
        return { solved: false, why: 'No strike.' };
    if (years <= 0)
        return { solved: false, why: 'Expired; volatility is no longer defined.' };
    const base = { forward, strike, years, rate, optionType };
    /*
     * The two walls. Below the discounted intrinsic there is no volatility that
     * reaches the quote, and above the discounted forward (for a call) or
     * strike (for a put) there is none either — the price exceeds the most the
     * contract can ever pay. Both are REFUSALS, not clamps: clamping to the
     * bound would hand back 0.1% or 1000% as though it were measured.
     */
    const intrinsic = intrinsicValue(base);
    if (price < intrinsic - PRICE_TOLERANCE) {
        return {
            solved: false,
            why: 'Price is below intrinsic value — a stale or crossed quote, not a volatility.',
        };
    }
    /*
     * AT intrinsic, there is nothing left to imply a volatility FROM.
     *
     * The time value is the only part of the price that carries volatility
     * information, and on a deep wing it is smaller than the exchange's own
     * tick: a 2,000-point ITM option a week out is worth its intrinsic plus a
     * fraction of a paisa, and every volatility from 1% to 1000% prices it to
     * the same number of paise. A solver handed that will still return
     * something, and whatever it returns is an artefact of where the bisection
     * happened to stop rather than a measurement.
     *
     * This is not a shortcoming of the solver that a better one would fix. The
     * market cannot pin that IV either; its quotes are in paise too.
     */
    if (price - intrinsic < PRICE_TOLERANCE) {
        return {
            solved: false,
            why: 'Priced at intrinsic value — no time value left to imply a volatility from.',
        };
    }
    const ceiling = black76Price({ ...base, vol: VOL_MAX });
    if (price > ceiling + PRICE_TOLERANCE) {
        return { solved: false, why: 'Price is above any volatility this model can reach.' };
    }
    let lo = VOL_MIN;
    let hi = VOL_MAX;
    for (let i = 0; i < MAX_ITERATIONS && hi - lo > VOL_TOLERANCE; i += 1) {
        const mid = (lo + hi) / 2;
        // Price is strictly increasing in vol, so the sign of the difference says
        // which half holds the root. The bracket can only shrink.
        if (black76Price({ ...base, vol: mid }) > price)
            hi = mid;
        else
            lo = mid;
    }
    return { solved: true, iv: (lo + hi) / 2 };
}
/**
 * IV and greeks for one option, from its market price.
 *
 * The one entry point a caller needs. Solving and evaluating are separate
 * functions because the IV surface is worth looking at on its own, but no
 * caller should be able to pass one option's price and another's vol.
 */
export function legGreeks(input) {
    const iv = impliedVol(input);
    if (!iv.solved)
        return { kind: 'UNSOLVABLE', why: iv.why };
    return { kind: 'OK', iv: iv.iv, greeks: greeksOf({ ...input, vol: iv.iv }) };
}
/**
 * The forward the market is actually using, from put-call parity.
 *
 * `F = K + (C - P) x e^(rT)`.
 *
 * For when there is no futures feed for the expiry — the common case here,
 * since the master feed subscribes the option chain and the future is a
 * separate instrument that may not be in the pool. Taken at the strike
 * NEAREST the money, where both legs are liquid and the bid-ask is tightest;
 * a far strike gives the same forward in theory and a noisy one in practice,
 * because the parity difference is then two small numbers subtracted.
 *
 * Self-consistent by construction: the forward recovered this way reprices
 * that strike's call and put at the SAME implied volatility, which a cash
 * index plus an assumed carry does not.
 */
export function forwardFromParity(input) {
    const { callPrice, putPrice, strike, years, rate } = input;
    if (!(callPrice > 0) || !(putPrice > 0) || !(strike > 0))
        return null;
    if (years < 0)
        return null;
    const forward = strike + (callPrice - putPrice) * Math.exp(rate * years);
    // A forward that is not a positive number means the two quotes cannot both
    // be real — a crossed book, or one side stale by minutes.
    return forward > 0 && Number.isFinite(forward) ? forward : null;
}
//# sourceMappingURL=greeks.js.map